import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import type { StoredNotification } from './store';
import type { NotificationsProps } from './types';

import Motion from '@snowit/ui/utils/Motion';

import useDomElement from '../useDomElement';
import useModals from '../useModals';
import Notification from './Notification';
import { useNotificationsStore } from './store';

import cx from 'classnames';
import s from './Notification.module.scss';

import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const Notifications = ({ position = 'topRight' }: NotificationsProps) => {
  const notifications = useNotificationsStore((state) => state.notifications);
  const removeNotification = useNotificationsStore((state) => state.removeNotification);
  const { showErrorV2 } = useModals();
  const timers = useRef(
    new Map<string, { notification: StoredNotification; timeout: ReturnType<typeof setTimeout> }>(),
  );
  const handledErrors = useRef(new WeakSet<StoredNotification>());

  useEffect(() => {
    const currentIds = new Set(notifications.map(({ id }) => id));

    for (const [id, timer] of timers.current) {
      if (!currentIds.has(id)) {
        clearTimeout(timer.timeout);
        timers.current.delete(id);
      }
    }

    for (const notification of notifications) {
      const { id, theme, duration = 2000 } = notification;

      if (theme === 'errorV2') {
        if (handledErrors.current.has(notification)) continue;
        handledErrors.current.add(notification);

        const timer = timers.current.get(id);
        if (timer) {
          clearTimeout(timer.timeout);
          timers.current.delete(id);
        }

        removeNotification(id);
        showErrorV2({
          code: notification.code || '',
          message: notification.message,
          stack: true,
        });
        continue;
      }

      const previousTimer = timers.current.get(id);
      if (previousTimer?.notification === notification) continue;
      if (previousTimer) clearTimeout(previousTimer.timeout);

      if (theme === 'error' || theme === 'marketing') {
        timers.current.delete(id);
        continue;
      }

      timers.current.set(id, {
        notification,
        timeout: setTimeout(() => removeNotification(id), duration),
      });
    }
  }, [notifications, removeNotification, showErrorV2]);

  useEffect(() => {
    return () => {
      for (const timer of timers.current.values()) clearTimeout(timer.timeout);
      timers.current.clear();
    };
  }, []);

  const root = useDomElement({ id: 'notifications' });
  const container = (
    <Motion>
      <AnimatePresence>
        <div className={cx(s.container, s[position])}>
          {notifications.map((notification) =>
            notification.theme === 'errorV2' ? null : (
              <Notification
                key={notification.id}
                position={position}
                {...notification}
                onClose={() => removeNotification(notification.id)}
              />
            ),
          )}
        </div>
      </AnimatePresence>
    </Motion>
  );

  return root ? (createPortal(container, root) as ReactElement) : null;
};

export default Notifications;
